import fixWebmDuration from 'fix-webm-duration';
import type { ChatAttachedFileProps } from 'src/content/Chat/utils';
import { APPS_NAV_LINK } from 'src/content/MiniExperts/constants';
import type { FileNode } from 'src/content/MiniExperts/SmartMeet/types/types';
import type { IBreadcrumbItem } from 'src/types/custom_component';
import { DEFAULT_CONTENT_ROLES } from 'src/types/enum';
import { AUDIO_CHUNK_DURATION, MAX_NAME_LENGTH } from 'src/utils/constants';
import logger from 'src/utils/logger';
import { getMeetingChunks, getMeetingMetadata } from 'src/utils/recordingDB';

export const getSmartMeetBreadcrumbs = (
  id: string,
  level: 1 | 2 | 3 = 3
): IBreadcrumbItem[] => {
  const base: IBreadcrumbItem[] = [
    {
      id: 'mini-apps',
      name: 'Apps',
      webUrl: APPS_NAV_LINK.MINI_EXPERTS
    },
    {
      id: 'smart-meet',
      name: 'Smart Meet',
      webUrl: APPS_NAV_LINK.SMART_MEET
    },
    {
      id: `smart-meet/${id}`,
      name: id,
      webUrl: `${APPS_NAV_LINK.SMART_MEET}/${id}`
    }
  ];

  return base.slice(0, level);
};

export const extractUploadedFiles = (data: ChatAttachedFileProps[]) => {
  const filesRoot = data?.data?.files;
  if (!filesRoot) {
    return [];
  }

  const flatten = (node: FileNode, collected: FileNode[] = []) => {
    if (node.url && node.name) {
      collected.push({
        id: node.id,
        name: node.name,
        type_id: node?.type_id,
        transaction_id: node.transaction_id,
        created_by: node.created_by
      });
    }

    if (node.children) {
      Object.values(node.children).forEach((child: FileNode) =>
        flatten(child, collected)
      );
    }

    return collected;
  };

  let allFiles: FileNode[] = [];
  Object.values(filesRoot).forEach((rootNode: FileNode) => {
    allFiles = allFiles.concat(flatten(rootNode));
  });

  return allFiles;
};

export const getFormattedTime = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export function msToTime(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export const getAudioDuration = async (
  audioBlob: Blob | null
): Promise<number> => {
  if (!audioBlob) return 0;
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    const url = URL.createObjectURL(audioBlob);

    audio.src = url;
    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url);
      resolve(Math.floor(audio.duration));
    });

    audio.addEventListener('error', (e) => {
      reject(e);
      URL.revokeObjectURL(url);
    });
  });
};

export const getNewMeetingName = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');

  const name = `${year}-${month}-${day} T ${hours}:${minutes}:${seconds}`;
  return name;
};

export const clampMeetingName = (name: string): string => {
  const trimmedName = name?.trim() ?? '';
  if (trimmedName.length <= MAX_NAME_LENGTH) {
    return trimmedName;
  }

  const dotIndex = trimmedName.lastIndexOf('.');
  const extension =
    dotIndex > 0 && trimmedName.length - dotIndex <= 6
      ? trimmedName.slice(dotIndex)
      : '';

  return `${trimmedName.slice(
    0,
    MAX_NAME_LENGTH - extension.length
  )}${extension}`;
};

export const userHasEditPermission = (role: DEFAULT_CONTENT_ROLES) => {
  const editableRoles = [
    DEFAULT_CONTENT_ROLES.CONTROLLER,
    DEFAULT_CONTENT_ROLES.CONTRIBUTOR
  ];
  return editableRoles.includes(role);
};

const audioBufferToWav = (buffer: AudioBuffer): Blob => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = buffer.length * blockAlign;
  const out = new ArrayBuffer(44 + dataSize);
  const view = new DataView(out);

  const w = (off: number, str: string) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(off + i, str.charCodeAt(i));
  };

  w(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  w(36, 'data');
  view.setUint32(40, dataSize, true);

  const channels = Array.from({ length: numChannels }, (_, ch) =>
    buffer.getChannelData(ch)
  );
  let pos = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const s = Math.max(-1, Math.min(1, channels[ch][i]));
      view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      pos += 2;
    }
  }

  return new Blob([out], { type: 'audio/wav' });
};

const mergeMultiSessionChunks = async (
  sortedChunks: any[],
  meetingName: string
): Promise<File> => {
  const sessionMap = new Map<string, Blob[]>();
  for (const chunk of sortedChunks) {
    const sid = chunk.sessionId || 'default';
    if (!sessionMap.has(sid)) sessionMap.set(sid, []);
    sessionMap.get(sid)!.push(chunk.blob);
  }

  const ctx = new AudioContext();
  const decoded: AudioBuffer[] = [];
  for (const [sid, blobs] of sessionMap) {
    const blob = new Blob(blobs, { type: 'audio/webm; codecs=opus' });
    try {
      decoded.push(await ctx.decodeAudioData(await blob.arrayBuffer()));
    } catch (err) {
      logger.error(`Failed to decode session ${sid}:`, err);
    }
  }
  await ctx.close();

  if (decoded.length === 0)
    throw new Error('No audio sessions could be decoded');

  const sampleRate = decoded[0].sampleRate;
  const channels = decoded[0].numberOfChannels;
  const totalLength = decoded.reduce((s, b) => s + b.length, 0);
  const offline = new OfflineAudioContext(channels, totalLength, sampleRate);

  let offset = 0;
  for (const buf of decoded) {
    const src = offline.createBufferSource();
    src.buffer = buf;
    src.connect(offline.destination);
    src.start(offset / sampleRate);
    offset += buf.length;
  }

  const rendered = await offline.startRendering();
  const wavBlob = audioBufferToWav(rendered);

  return new File([wavBlob], `${meetingName}.wav`, {
    type: 'audio/wav',
    lastModified: Date.now()
  });
};

export const getAudioFile = async (meetingName: string) => {
  const chunks = await getMeetingChunks(meetingName);

  if (!chunks || chunks.length === 0) return null;

  let metadata = null;
  try {
    metadata = await getMeetingMetadata(meetingName);
  } catch (e) {
    logger.warn('Metadata store not found or entry missing', e);
  }

  const sorted = chunks.sort((a, b) => a.chunkIndex - b.chunkIndex);

  const sessionIds = new Set(sorted.map((c) => c.sessionId || 'default'));
  const hasMultipleSessions = sessionIds.size > 1;

  if (hasMultipleSessions) {
    return mergeMultiSessionChunks(sorted, meetingName);
  }

  const blobs = sorted.map((c) => c.blob);
  const baseBlob = new Blob(blobs, { type: 'audio/webm; codecs=opus' });

  const duration = metadata?.duration || chunks.length * AUDIO_CHUNK_DURATION;

  return new Promise<File>((resolve) => {
    fixWebmDuration(baseBlob, duration, (fixedBlob) => {
      const file = new File([fixedBlob], `${meetingName}.webm`, {
        type: 'audio/webm; codecs=opus',
        lastModified: Date.now()
      });
      resolve(file);
    });
  });
};

export const MAX_DURATION_SECONDS = 7200;

export const resolveProcessingRoute = (meetingId: number | string) => {
  if (meetingId && window.location.pathname.includes('/processing')) {
    window.history.replaceState(
      null,
      '',
      `${APPS_NAV_LINK.SMART_MEET}/${meetingId}`
    );
  }
};
